import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

export default function TresoreriePage() {
  const [mouvements, setMouvements] = useState([]);
  const [soldes, setSoldes] = useState({ solde_caisse: 0, solde_banque: 0, total_disponible: 0, mouvements_jour: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ libelle: '', type: 'entree', montant: '', compte: 'caisse', categorie: '' });
  const [filterCompte, setFilterCompte] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get('/comptabilite/tresorerie'), api.get('/comptabilite/tresorerie/soldes')]);
      setMouvements(m.data);
      setSoldes(s.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post('/comptabilite/tresorerie', form); setShowForm(false); load(); } catch {}
  };

  const filtered = mouvements.filter(m => !filterCompte || m.compte === filterCompte);

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Solde caisse</span></div>
          <div className="stat-card-value">{Number(soldes.solde_caisse).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Solde banque</span></div>
          <div className="stat-card-value">{Number(soldes.solde_banque).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total disponible</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{Number(soldes.total_disponible).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Mouvements aujourd'hui</span></div>
          <div className="stat-card-value">{Number(soldes.mouvements_jour).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="content-card-title">Mouvements de tresorerie</div>
            <select className="gss-input" value={filterCompte} onChange={e => setFilterCompte(e.target.value)} style={{ width: 120 }}>
              <option value="">Tous</option><option value="caisse">Caisse</option><option value="banque">Banque</option>
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Nouveau mouvement</button>
        </div>

        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Date</th><th>Libelle</th><th>Compte</th><th>Type</th><th>Montant</th><th>Categorie</th></tr></thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id}>
                    <td>{m.date_mouvement?.split('T')[0]}</td>
                    <td style={{ fontWeight: 600 }}>{m.libelle}</td>
                    <td><span className="gss-badge" style={{ background: m.compte === 'caisse' ? '#f57c0020' : '#1565c020', color: m.compte === 'caisse' ? '#f57c00' : '#1565c0' }}>{m.compte}</span></td>
                    <td><span style={{ color: m.type === 'entree' ? '#2e7d32' : '#DC2626', fontWeight: 600 }}>{m.type === 'entree' ? '+ Entree' : '- Sortie'}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: m.type === 'entree' ? '#2e7d32' : '#DC2626' }}>{Number(m.montant).toLocaleString()} F</td>
                    <td style={{ fontSize: '0.8rem', color: '#888' }}>{m.categorie || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Nouveau mouvement</h2>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Libelle *</label><input className="gss-input" required value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} /></div>
                  <div className="gss-field"><label>Type *</label>
                    <select className="gss-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="entree">Entree</option><option value="sortie">Sortie</option>
                    </select>
                  </div>
                  <div className="gss-field"><label>Montant *</label><input className="gss-input" type="number" required min="0" value={form.montant} onChange={e => setForm({ ...form, montant: e.target.value })} /></div>
                  <div className="gss-field"><label>Compte *</label>
                    <select className="gss-input" value={form.compte} onChange={e => setForm({ ...form, compte: e.target.value })}>
                      <option value="caisse">Caisse</option><option value="banque">Banque</option>
                    </select>
                  </div>
                  <div className="gss-field"><label>Categorie</label><input className="gss-input" value={form.categorie} onChange={e => setForm({ ...form, categorie: e.target.value })} placeholder="Exploitation, Investissement..." /></div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
